// A voice's audio in the browser: a recording made a WAV file (live saves its recordings as WAV,
// `useAudioRecorder`), and a file's length read before it goes up (live's
// `validateVoiceFile`).

/** The samples of a decoded recording: one channel's floats at their rate. */
export interface PcmSamples {
  rate: number;
  samples: Float32Array;
}

/** 16-bit PCM mono WAV of `pcm`: the type every browser plays and the server takes. */
export function encodeWav({ rate, samples }: PcmSamples): Blob {
  const bytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + bytes);
  const view = new DataView(buffer);
  const text = (at: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(at + index, value.charCodeAt(index));
    }
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + bytes, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // one channel
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); // bytes a second
  view.setUint16(32, 2, true); // bytes a frame
  view.setUint16(34, 16, true); // bits a sample
  text(36, "data");
  view.setUint32(40, bytes, true);
  samples.forEach((sample, index) => {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + index * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
  });
  return new Blob([buffer], { type: "audio/wav" });
}

/** A recording's channels mixed down to one, at its rate. */
export function mixDown(buffer: AudioBuffer): PcmSamples {
  const samples = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < data.length; index += 1) {
      samples[index]! += data[index]! / buffer.numberOfChannels;
    }
  }
  return { rate: buffer.sampleRate, samples };
}

/** A recorded blob (WebM or MP4, whatever the browser records) as a WAV file. */
export async function recordingAsWav(recorded: Blob): Promise<File> {
  const context = new AudioContext();
  try {
    const decoded = await context.decodeAudioData(await recorded.arrayBuffer());
    // Live names its recordings `recording.wave`; ours keeps the extension the type has.
    return new File([encodeWav(mixDown(decoded))], "recording.wav", { type: "audio/wav" });
  } finally {
    void context.close();
  }
}

/** A file's length in seconds as the browser reads it, or null when it cannot. */
export function readDuration(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (seconds: number | null) => {
      URL.revokeObjectURL(url);
      audio.removeAttribute("src");
      resolve(seconds);
    };
    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", () =>
      done(Number.isFinite(audio.duration) ? audio.duration : null),
    );
    audio.addEventListener("error", () => done(null));
    audio.src = url;
  });
}
